/**
 * ButtonGroup Component
 * A group of buttons for selecting a single option (like radio buttons but styled as buttons)
 * Ideal for 2-4 options where a dropdown would be overkill
 */

import React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/cn';

const buttonGroupItemVariants = cva(
  'inline-flex items-center justify-center font-medium transition-colors focus:outline-none focus:z-10',
  {
    variants: {
      size: {
        sm: 'px-3 py-1.5 text-sm',
        md: 'px-4 py-2 text-sm',
        lg: 'px-5 py-2.5 text-base',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
);

export interface ButtonGroupOption {
  value: string;
  label: string;
  icon?: React.ReactNode;
}

export interface ButtonGroupProps extends VariantProps<typeof buttonGroupItemVariants> {
  options: ButtonGroupOption[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
}

export const ButtonGroup: React.FC<ButtonGroupProps> = ({
  options,
  value,
  onChange,
  size = 'md',
  disabled = false,
  className,
}) => {
  return (
    <div className={cn('inline-flex rounded-lg border border-input bg-card', className)}>
      {options.map((option, index) => {
        const isSelected = option.value === value;
        const isFirst = index === 0;
        const isLast = index === options.length - 1;

        return (
          <button
            key={option.value}
            type="button"
            onClick={() => !disabled && onChange(option.value)}
            disabled={disabled}
            className={cn(
              buttonGroupItemVariants({ size }),
              isFirst && 'rounded-l-lg',
              isLast && 'rounded-r-lg',
              !isLast && 'border-r border-input',
              isSelected
                ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                : 'bg-card text-foreground hover:bg-accent hover:text-accent-foreground',
              disabled && 'cursor-not-allowed opacity-50'
            )}
          >
            {option.icon && <span className="mr-1.5">{option.icon}</span>}
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

export default ButtonGroup;
